import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Button,
  Select,
  SelectItem,
} from '@heroui/react';
import { Upload, X, FileText, ImageIcon, CheckCircle, Loader2 } from 'lucide-react';
import { useCreateProject, useIntakeProject } from '@/api/projects';
import { apiClient } from '@/api/client';
import { ProjectType } from '@estimator/shared';
import type { ProjectTypeEnum } from '@estimator/shared';
import CompanyCombobox from '@/components/shared/CompanyCombobox';

// ─── Constants ────────────────────────────────────────────────────────────────

const PROJECT_TYPES: { value: ProjectTypeEnum; label: string }[] = [
  { value: ProjectType.RESIDENTIAL, label: 'Residential' },
  { value: ProjectType.COMMERCIAL, label: 'Commercial' },
  { value: ProjectType.INDUSTRIAL, label: 'Industrial' },
  { value: ProjectType.MIXED_USE, label: 'Mixed Use' },
  { value: ProjectType.RETAIL, label: 'Retail' },
  { value: ProjectType.HEALTHCARE, label: 'Healthcare' },
  { value: ProjectType.EDUCATION, label: 'Education' },
];

const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

// ─── Progress steps ───────────────────────────────────────────────────────────

type ProgressStep = 'creating' | 'uploading' | 'analysing' | 'done';

const STEPS: { key: ProgressStep; label: string }[] = [
  { key: 'creating', label: 'Creating project…' },
  { key: 'uploading', label: 'Uploading files…' },
  { key: 'analysing', label: 'Analysing with AI…' },
  { key: 'done', label: 'Done!' },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface NewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NewProjectModal({ isOpen, onClose }: NewProjectModalProps) {
  const navigate = useNavigate();
  const createProject = useCreateProject();
  const intakeProject = useIntakeProject();

  // Form state
  const [clientId, setClientId] = useState<string | null>(null);
  const [specifierId, setSpecifierId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState('');
  const [address, setAddress] = useState('');
  const [projectType, setProjectType] = useState<ProjectTypeEnum>(ProjectType.RESIDENTIAL);

  // File state
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Progress state
  const [currentStep, setCurrentStep] = useState<ProgressStep | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isSubmitting = currentStep !== null && currentStep !== 'done';
  const hasFiles = files.length > 0;

  // ─── File handlers ──────────────────────────────────────────────────────────

  const addFiles = useCallback((incoming: FileList | File[]) => {
    const valid = Array.from(incoming).filter((f) => ACCEPTED_TYPES.includes(f.type));
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name + f.size));
      return [...prev, ...valid.filter((f) => !existing.has(f.name + f.size))];
    });
  }, []);

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    e.target.value = '';
  };

  // ─── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!clientId || !projectName.trim()) return;
    setError(null);

    try {
      // Step 1 — create project
      setCurrentStep('creating');
      const project = await createProject.mutateAsync({
        client_id: clientId!,
        specifier_id: specifierId || undefined,
        project_name: projectName.trim(),
        address: address.trim() || undefined,
        project_type: projectType,
      });

      // Fire-and-forget: fetch exchange rate and save to project
      apiClient.get('/exchange-rates/usd-mxn').then(({ data }) => {
        apiClient.patch(`/projects/${project.id}`, {
          mxn_exchange_rate: data.rate,
          mxn_rate_fetched_at: data.fetched_at,
          mxn_bank_spread: 0,
        });
      }).catch(() => { /* rate will be set manually in project settings */ });

      if (!hasFiles) {
        // No files — navigate directly
        navigate(`/projects/${project.id}`);
        handleClose();
        return;
      }

      // Step 2 — upload files
      setCurrentStep('uploading');
      // Brief artificial pause for UX feedback between steps
      await new Promise((r) => setTimeout(r, 400));

      // Step 3 — AI analysis
      setCurrentStep('analysing');
      await intakeProject.mutateAsync({ projectId: project.id, files });
      // Simulate extra analysis time so the step is visible
      await new Promise((r) => setTimeout(r, 800));

      // Step 4 — done
      setCurrentStep('done');
      await new Promise((r) => setTimeout(r, 700));

      navigate(`/projects/${project.id}`);
      handleClose();
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { data?: { message?: string; error?: string } };
        message?: string;
      };
      const msg =
        axiosError?.response?.data?.message ??
        axiosError?.response?.data?.error ??
        axiosError?.message ??
        'Something went wrong. Please try again.';
      setError(msg);
      setCurrentStep(null);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setClientId(null);
    setSpecifierId(null);
    setProjectName('');
    setAddress('');
    setProjectType(ProjectType.RESIDENTIAL);
    setFiles([]);
    setCurrentStep(null);
    setError(null);
    onClose();
  };

  // ─── Progress overlay ────────────────────────────────────────────────────────

  const stepIndex = currentStep ? STEPS.findIndex((s) => s.key === currentStep) : -1;

  if (currentStep !== null) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        isDismissable={false}
        hideCloseButton
        classNames={{
          backdrop: 'bg-black/70 backdrop-blur-sm',
          base: 'border border-[var(--border)]',
        }}
        style={{ '--modal-bg': 'var(--bg-surface)' } as React.CSSProperties}
      >
        <ModalContent style={{ backgroundColor: 'var(--bg-surface)' }}>
          <ModalBody className="py-10 px-8 flex flex-col items-center gap-8">
            <div className="flex flex-col gap-3 w-full">
              {STEPS.map((step, i) => {
                const isDone = i < stepIndex || currentStep === 'done';
                const isActive = i === stepIndex && currentStep !== 'done';
                return (
                  <div key={step.key} className="flex items-center gap-3">
                    {isDone ? (
                      <CheckCircle size={18} style={{ color: 'var(--green)' }} />
                    ) : isActive ? (
                      <Loader2
                        size={18}
                        className="animate-spin"
                        style={{ color: 'var(--accent)' }}
                      />
                    ) : (
                      <div
                        className="w-[18px] h-[18px] rounded-full border-2"
                        style={{ borderColor: 'var(--border-em)' }}
                      />
                    )}
                    <span
                      className="text-sm"
                      style={{
                        color: isDone
                          ? 'var(--green)'
                          : isActive
                            ? 'var(--text-primary)'
                            : 'var(--text-muted)',
                        fontWeight: isActive ? 500 : 400,
                      }}
                    >
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  // ─── Main form ───────────────────────────────────────────────────────────────

  const inputClassNames = {
    inputWrapper:
      'border border-[var(--border)] bg-[var(--bg-panel)] data-[hover=true]:border-[var(--border-em)] data-[focus=true]:border-[var(--accent)]',
    input: 'text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
    label: 'text-[var(--text-secondary)] text-sm',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      classNames={{
        backdrop: 'bg-black/70 backdrop-blur-sm',
        base: 'border border-[var(--border)]',
        header: 'border-b border-[var(--border)]',
        footer: 'border-t border-[var(--border)]',
      }}
    >
      <ModalContent style={{ backgroundColor: 'var(--bg-surface)' }}>
        <ModalHeader>
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
            New Project
          </h2>
        </ModalHeader>

        <ModalBody className="py-5 flex flex-col gap-4">
          {/* Client */}
          <CompanyCombobox value={clientId} onChange={setClientId} label="Client" isRequired />

          {/* Specifier */}
          <CompanyCombobox value={specifierId} onChange={setSpecifierId} label="Specifier (Architect/Designer)" />

          {/* Project Name */}
          <Input
            label="Project Name"
            placeholder="123 Main St — Residential Build"
            value={projectName}
            onValueChange={setProjectName}
            isRequired
            variant="bordered"
            classNames={inputClassNames}
          />

          {/* Address */}
          <Input
            label="Address"
            placeholder="123 Main Street, Sydney NSW 2000"
            value={address}
            onValueChange={setAddress}
            variant="bordered"
            classNames={inputClassNames}
          />

          {/* Project Type */}
          <Select
            label="Project Type"
            selectedKeys={[projectType]}
            onSelectionChange={(keys) => {
              const val = Array.from(keys)[0] as ProjectTypeEnum;
              if (val) setProjectType(val);
            }}
            variant="bordered"
            classNames={{
              trigger:
                'border border-[var(--border)] bg-[var(--bg-panel)] data-[hover=true]:border-[var(--border-em)]',
              value: 'text-[var(--text-primary)]',
              label: 'text-[var(--text-secondary)] text-sm',
              popoverContent: 'border border-[var(--border)] bg-[var(--bg-panel)]',
            }}
          >
            {PROJECT_TYPES.map((t) => (
              <SelectItem
                key={t.value}
                classNames={{ base: 'data-[hover=true]:bg-[var(--bg-hover)]' }}
                style={{ color: 'var(--text-primary)' }}
              >
                {t.label}
              </SelectItem>
            ))}
          </Select>

          {/* File dropzone */}
          <div className="flex flex-col gap-2">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Drawings / Documents{' '}
              <span style={{ color: 'var(--text-muted)' }}>(optional)</span>
            </p>

            <div
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className="relative rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 py-7 px-4 transition-colors cursor-pointer focus:outline-none"
              style={{
                borderColor: isDragging ? 'var(--accent)' : 'var(--border-em)',
                backgroundColor: isDragging ? 'rgba(232, 160, 32, 0.06)' : 'var(--bg-panel)',
              }}
            >
              <Upload size={24} style={{ color: isDragging ? 'var(--accent)' : 'var(--text-muted)' }} />
              <p className="text-sm text-center" style={{ color: 'var(--text-secondary)' }}>
                Drag &amp; drop PDFs or images here
              </p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                or click to browse
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                className="sr-only"
                onChange={handleFileInputChange}
              />
            </div>

            {/* File list */}
            {files.length > 0 && (
              <div className="flex flex-col gap-1.5 mt-1">
                {files.map((file, i) => {
                  const isPdf = file.type === 'application/pdf';
                  return (
                    <div
                      key={`${file.name}-${i}`}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg"
                      style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border)' }}
                    >
                      {isPdf ? (
                        <FileText size={14} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                      ) : (
                        <ImageIcon size={14} style={{ color: 'var(--blue)', flexShrink: 0 }} />
                      )}
                      <span
                        className="text-xs flex-1 truncate font-ui"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {file.name}
                      </span>
                      <span className="text-xs shrink-0" style={{ color: 'var(--text-muted)' }}>
                        {(file.size / 1024).toFixed(0)} KB
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(i);
                        }}
                        className="focus:outline-none shrink-0"
                      >
                        <X size={14} style={{ color: 'var(--text-muted)' }} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div
              className="text-sm px-3 py-2 rounded-lg border"
              style={{
                color: 'var(--red)',
                backgroundColor: 'rgba(248, 113, 113, 0.08)',
                borderColor: 'rgba(248, 113, 113, 0.2)',
              }}
            >
              {error}
            </div>
          )}
        </ModalBody>

        <ModalFooter className="gap-2">
          <Button
            variant="flat"
            onPress={handleClose}
            style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--bg-panel)' }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSubmit}
            isDisabled={!clientId || !projectName.trim()}
            className="font-medium"
          >
            {hasFiles ? 'Analyse with AI' : 'Create Project'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
